import type {
  AdminCompanyRow,
  AdminCompanyMemberRow,
  ActionLogRow,
  ActionTarget,
  CompanyRole,
  CompanyState,
  Currency,
  CurrencyRow,
  RateSource,
  AdminStats,
  AdminSystemStatus,
  AdminUserRow,
  AnalyticsMeta,
  AnalyticsQuery,
  AnalyticsReport,
  AuthMethodsInfo,
  DispatchBoard,
  DispatchQuery,
  StaffRole,
  SecretRow,
  ExportKind,
  LeaderBoard,
  AvailabilityInput,
  AvailabilityRow,
  CargoListItem,
  CargoMapPoint,
  FeedScope,
  ProfileType,
  FeatureMap,
  NotificationRow,
  PlatformPulse,
  FeatureRuleRow,
  CompanyMemberCreateInput,
  CompanyMemberRow,
  CrewRow,
  FleetRow,
  FleetMatchRow,
  VehicleTripRow,
  CompanyOverview,
  PatchVehicleInput,
  ReassignOrderInput,
  ExpenseInput,
  ExpenseRow,
  MyCompanyRow,
  OrderEconomics,
  PeriodSummary,
  ContractInput,
  ContractLevelRow,
  ContractMemberRow,
  ContractOverrideRow,
  ContractRow,
  CreateCargoInput,
  UpdateCargoInput,
  CargoDraftParsed,
  CargoDraftView,
  SourceChatRow,
  IngestedPostRow,
  UserbotView,
  DealBreakdown,
  EntityType,
  FaqSection,
  FaqSectionInput,
  FileRow,
  GroupKind,
  GroupMemberRow,
  GroupRow,
  MyCargoRow,
  OrderStatusEventRow,
  PlatformSettings,
  PublicProfile,
  RateHint,
  SimilarCargos,
  NotifyPrefs,
  VehicleKind,
  VerifyState,
  BroadcastInput,
  BroadcastResult,
  BroadcastRow,
  RecallResult,
  Role,
  TrackPointRow,
  TransportMode,
  BodyType,
  FeedLayout,
} from 'shared';
import { tg } from './telegram';

const BASE = import.meta.env.VITE_API_URL ?? '';
const TOKEN_KEY = 'gruzbot_token';
/**
 * Свой токен администратора на время просмотра чужими глазами.
 *
 * Отдельный ключ, а не память в состоянии React: страницу перезагружают,
 * приложение переоткрывают, и без этого администратор остался бы чужими
 * глазами навсегда — с единственным выходом через полный выход из учётной
 * записи и повторный вход.
 */
const OWN_TOKEN_KEY = 'gruzbot_own_token';

/** Одна сделка в отчёте — строка таблицы и строка выгрузки. */
export interface DealRow {
  orderId: string;
  cargoId: string;
  number: string;
  title: string;
  status: string;
  createdAt: string;
  closedAt: string;
  fromRegion: string;
  toRegion: string;
  route: string;
  distanceKm: number;
  weightKg: number;
  bodyType: string;
  priceRub: number;
  shipperTotalRub: number;
  driverPayoutRub: number;
  buyerFeeRub: number;
  sellerFeeRub: number;
  carrierExpenses: number;
  shipperExpenses: number;
  driverId: string;
  driverName: string;
  logistId: string;
  logistName: string;
  vehicleId: string | null;
  vehiclePlate: string | null;
  companyId: string | null;
  companyName: string | null;
}

/** Пустые фильтры в строку запроса не попадают — иначе сервер их разбирает. */
function qs(q: object): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) p.set(k, String(v));
  return p.toString();
}

/** Имя файла из Content-Disposition, если сервер его прислал. */
function fileNameFrom(res: Response): string | null {
  const raw = res.headers.get('content-disposition') ?? '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(raw);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Сохранение blob под нужным именем.
 *
 * Ссылку освобождаем не сразу: Safari успевает отменить скачивание, если
 * URL отзывается в том же кадре, и файл молча не появляется.
 */
function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// localStorage, а не sessionStorage: в вебе логист не должен вводить пароль
// в каждой новой вкладке. В Telegram webview хранилище всё равно эфемерное.
let token: string | null = localStorage.getItem(TOKEN_KEY);

function setToken(value: string | null) {
  token = value;
  if (value) localStorage.setItem(TOKEN_KEY, value);
  else localStorage.removeItem(TOKEN_KEY);
}

export function hasToken(): boolean {
  return token !== null;
}

export function getToken(): string | null {
  return token;
}

export function logout() {
  setToken(null);
  localStorage.removeItem(OWN_TOKEN_KEY);
}

/** Смотрим ли мы сейчас чужими глазами. */
export function isViewingAs(): boolean {
  return localStorage.getItem(OWN_TOKEN_KEY) !== null;
}

/** Перейти к просмотру глазами пользователя, отложив свой токен. */
export function startViewAs(viewToken: string) {
  // Свой токен откладываем только в первый раз: вложенных просмотров
  // сервер всё равно не даёт, а перезапись потеряла бы дорогу назад.
  if (!localStorage.getItem(OWN_TOKEN_KEY) && token) {
    localStorage.setItem(OWN_TOKEN_KEY, token);
  }
  setToken(viewToken);
}

/** Вернуться в свою учётную запись. */
export function stopViewAs() {
  const own = localStorage.getItem(OWN_TOKEN_KEY);
  localStorage.removeItem(OWN_TOKEN_KEY);
  if (own) setToken(own);
}

/** Промах в конкретном поле формы: сервер называет поле, а не только беду. */
export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Машинный код причины отказа, если сервер его прислал. */
    readonly reason?: string,
    /** Бот, чей токен прошит на сервере — для диагностики ошибки подписи. */
    readonly expectedBot?: string | null,
    /**
     * Разбор по полям. Без него форма показывает «проверьте поля» и молчит
     * о том, какое именно, — а человек ищет промах перечитыванием.
     */
    readonly issues?: FieldIssue[],
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // Content-Type только при наличии тела: Fastify отвечает 400
      // «Body cannot be empty», если заголовок есть, а тела нет.
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}) as Record<string, unknown>);

  if (!res.ok) {
    // Токен протух — сбрасываем, интерфейс покажет форму входа.
    if (res.status === 401 && token) setToken(null);
    const e = body as {
      error?: string;
      reason?: string;
      expectedBot?: string | null;
      issues?: FieldIssue[];
    };
    throw new ApiError(
      e.error ?? `Ошибка ${res.status}`,
      res.status,
      e.reason,
      e.expectedBot,
      e.issues,
    );
  }

  return body as T;
}

/** Строка истории заявки — то же событие, но без служебных полей журнала. */
/** Валюта, включённая на площадке, с действующим курсом к рублю. */
export interface CurrencyOption {
  code: Currency;
  name: string;
  sign: string;
  deal: boolean;
  display: boolean;
  rate: number | null;
}

export interface CargoHistoryRow {
  id: string;
  action: string;
  actorName: string;
  details: string | null;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  createdAt: string;
}

/** Одни торги, в которых у меня есть живая ставка. */
export interface MyBidRow {
  cargoId: string;
  title: string;
  currency: Currency;
  bidEndsAt: string | null;
  bestBid: number | null;
  myBid: number;
  leading: boolean;
  nextBid: number | null;
}

export interface Session {
  token: string;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    login: string | null;
    phone: string | null;
    role: Role;
    profileType: ProfileType | null;
    isAdmin: boolean;
    /** Должность в команде площадки: от неё зависят диспетчерские разделы. */
    staffRole: StaffRole;
    hasPassword: boolean;
    needsOnboarding: boolean;
    /** Может ли сам брать грузы. У наёмного водителя без права — нет. */
    canTakeCargo: boolean;
    /** Управляет хотя бы одной компанией — то есть диспетчер, а не сотрудник. */
    managesCompany: boolean;
  };
}

export interface Me {
  id: string;
  profileType: ProfileType | null;
  login: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: Role;
  isAdmin: boolean;
  staffRole: StaffRole;
  hasPassword: boolean;
  hasTelegram: boolean;
  /** На этот аккаунт сейчас смотрит администратор — интерфейс покажет полосу. */
  viewedByAdmin?: boolean;
  entityType: EntityType;
  rating: number;
  ratingCount: number;
  cargosPublished: number;
  ordersDone: number;
  ordersCancelled: number;
  company: { id: string; name: string; inn: string; verified: boolean } | null;
  vehicles: Vehicle[];
  subscriptions: Subscription[];
  /** Кузова в быстрых кнопках над лентой — свои у каждого. */
  favoriteBodyTypes: BodyType[];
  /** Может ли брать грузы. У наёмного водителя без права — нет. */
  canTakeCargo: boolean;
  /** Управляет хотя бы одной компанией — то есть диспетчер, а не сотрудник. */
  managesCompany: boolean;
  /** В какой валюте человек хочет видеть суммы. Пусто — в валюте сделки. */
  displayCurrency: string | null;
  /** Раскладка ленты на широком экране. */
  feedLayout: FeedLayout;
  stats: { activeOrders: number; doneOrders: number };
}

export interface MyOffer {
  id: string;
  priceRub: number;
  /** Ставка и валюта — в валюте заявки: своей у отклика нет. */
  price: number;
  currency: Currency;
  status: string;
  createdAt: string;
  cargo: {
    id: string;
    title: string;
    fromAddress: string;
    toAddress: string;
    priceRub: number;
    price: number;
    currency: Currency;
    status: string;
    distanceKm: number | null;
    loadDateFrom: string;
  };
}

/** Место из справочника или геокодера. */
export interface Place {
  name: string;
  region: string;
  country: string;
  lat: number;
  lng: number;
}

export interface Vehicle {
  id: string;
  /** Машина, прицеп, полуприцеп или платформа. */
  kind?: VehicleKind;
  plate: string;
  bodyType: string;
  capacityKg: number;
  volumeM3: number | null;
  adr: boolean;
  craneCapacityKg: number | null;
  craneReachM: number | null;
  /** Габариты и полная масса гружёной машины — для грузового профиля маршрутизации. */
  heightM: number | null;
  widthM: number | null;
  lengthM: number | null;
  grossWeightKg: number | null;
}

export interface Subscription {
  id: string;
  fromRegion: string | null;
  toRegion: string | null;
  bodyTypes: string[];
  minPrice: number | null;
}

/** Кому звонить по рейсу и на чём везут. */
export interface OrderContact {
  role: 'MANAGER' | 'DRIVER';
  name: string;
  phone: string | null;
  tgUsername: string | null;
}

export interface CargoDetail extends CargoListItem {
  comment: string | null;
  /** Конец окна погрузки, если задан («грузим 9–11 сен»). */
  loadDateTo: string | null;
  /** Курс валюты сделки к рублю на момент публикации. Снимок, не текущий. */
  rateToRub: number;
  /** Точки и режим — нужны форме правки, чтобы показать опубликованное. */
  transportMode: TransportMode;
  fromLat: number;
  fromLng: number;
  fromCountry: string | null;
  toLat: number;
  toLng: number;
  toCountry: string | null;
  vatIncluded: boolean;
  status: string;
  isAuthor: boolean;
  myOffer: { id: string; priceRub: number; price: number; status: string } | null;
  /** Группа закрытой публикации. Пусто — заявка ушла в общую ленту. */
  audience: { id: string; name: string } | null;
  contacts: {
    phone: string | null;
    tgUsername: string | null;
    contactPhone?: string | null;
  } | null;
  /**
   * Точный адрес забора и доставки. `null` — либо не заполнен, либо
   * заявка ещё не ваша: посторонним сервер его не отдаёт (см. карточку
   * груза на сервере, правило то же, что у контактов).
   */
  fromAddressDetail: string | null;
  toAddressDetail: string | null;
  /**
   * Актуальный рейс по заявке — только сторонам сделки. Нужен, чтобы
   * заказчик мог подтвердить приёмку прямо из карточки (её открывают
   * из напоминания). Пусто — рейса нет или вы не его сторона.
   */
  order: { id: string; status: string; confirmedAt: string | null } | null;
}

export interface Offer {
  id: string;
  priceRub: number;
  /** Ставка в валюте заявки — в ней её и называл перевозчик. */
  price: number;
  comment: string | null;
  status: string;
  createdAt: string;
  driver: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    entityType: EntityType;
    rating: number;
    ratingCount: number;
    ordersDone: number;
    ordersCancelled: number;
    company: { name: string; verified: boolean } | null;
  };
  vehicle: Vehicle | null;
}

export interface Order {
  id: string;
  status: string;
  /*
   * Деньги рейса — в рублях учёта, а рядом валюта и курс сделки.
   *
   * Все три зафиксированы в момент заключения и не меняются никогда:
   * ни при движении курса, ни при смене валюты отображения. Сделка на
   * 450 000 ₸ остаётся сделкой на 450 000 ₸.
   */
  currency: Currency;
  rateToRub: number;
  /** Договорная ставка в валюте сделки — ровно та, что назвали стороны. */
  price: number;
  priceRub: number;
  buyerFeeRub: number;
  sellerFeeRub: number;
  driverPayoutRub: number;
  shipperTotalRub: number;
  /** Идёт ли сейчас трансляция геопозиции из Telegram. */
  trackLive: boolean;
  cancelReason: string | null;
  cancelledByName: string | null;
  confirmedAt: string | null;
  confirmedByName: string | null;
  /** Заказчик может принять груз: рейс завершён и ещё не подтверждён. */
  canConfirm: boolean;
  createdAt: string;
  myRole: 'DRIVER' | 'LOGIST';
  /** Суммы скрыты: наёмный водитель без права видеть деньги компании. */
  ratesHidden?: boolean;
  companyId: string | null;
  managerId: string | null;
  driverId: string;
  cargo: { id: string; title: string; fromAddress: string; toAddress: string };
  driver: { firstName: string | null; lastName: string | null; phone: string | null; tgUsername: string | null };
  logist: { firstName: string | null; lastName: string | null; phone: string | null; tgUsername: string | null };
  lastPoint: { lat: number; lng: number; recordedAt: string } | null;
  /**
   * Кому звонить по рейсу. У компании это диспетчер, у частника — он сам:
   * водителя в дороге дёргать нельзя, он за рулём и не знает договорённостей.
   */
  contact: OrderContact;
  /** Чем везут. Заказчик узнаёт машину по цвету, марке и номеру. */
  vehicle: {
    plate: string;
    make: string | null;
    model: string | null;
    color: string | null;
    kind: string;
    bodyType: string;
  } | null;
}

export const api = {
  authMethods: () => request<AuthMethodsInfo>('/auth/methods'),

  async loginTelegram(): Promise<Session> {
    const initData = tg?.initData;
    if (!initData) {
      throw new ApiError('Откройте приложение через Telegram-бота', 401, 'EMPTY');
    }

    const res = await request<Session>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    });

    setToken(res.token);
    return res;
  },

  async loginPassword(login: string, password: string): Promise<Session> {
    const res = await request<Session>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    });
    setToken(res.token);
    return res;
  },

  async register(data: {
    login: string;
    password: string;
    firstName: string;
    lastName?: string;
    phone?: string;
    email?: string;
  }): Promise<Session> {
    const res = await request<Session>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    setToken(res.token);
    return res;
  },

  setCredentials: (login: string, password: string) =>
    request<{ ok: true; login: string }>('/me/credentials', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),

  /** Код и ссылка для привязки Telegram к текущему аккаунту. */
  linkTelegram: () =>
    request<{
      code: string;
      expiresInMin: number;
      bot: string | null;
      deepLink: string | null;
    }>('/me/link/telegram', { method: 'POST' }),

  /** Влить существующий веб-аккаунт в текущий (пришедший из Telegram). */
  linkPassword: (login: string, password: string) =>
    request<{ ok: true }>('/me/link/password', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/me/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  settings: () => request<PlatformSettings>('/settings'),

  faq: () => request<{ items: FaqSection[] }>('/faq'),

  me: () => request<Me>('/me'),

  updateMe: (data: {
    role?: string;
    phone?: string;
    inn?: string;
    entityType?: EntityType;
    favoriteBodyTypes?: BodyType[];
    feedLayout?: FeedLayout;
    /** Пустая строка — сброс на «в валюте сделки». */
    displayCurrency?: string;
  }) => request<Me>('/me', { method: 'PATCH', body: JSON.stringify(data) }),

  myCargos: (status?: 'active' | 'archive') =>
    request<{ items: MyCargoRow[] }>(
      `/my/cargos${status ? `?status=${status}` : ''}`,
    ),

  myOffers: () => request<{ items: MyOffer[] }>('/my/offers'),

  rateHint: (p: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') qs.set(k, String(v));
    return request<RateHint>(`/market/rate-hint?${qs}`);
  },

  availability: (p: Record<string, string | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v) qs.set(k, v);
    return request<{ items: AvailabilityRow[] }>(`/availability?${qs}`);
  },

  addAvailability: (data: AvailabilityInput) =>
    request<{ id: string }>('/availability', { method: 'POST', body: JSON.stringify(data) }),

  removeAvailability: (id: string) =>
    request<{ ok: true }>(`/availability/${id}`, { method: 'DELETE' }),

  publicProfile: (userId: string) =>
    request<PublicProfile>(`/users/${userId}/profile`),

  /* ---------- Администрирование ---------- */

  admin: {
    stats: () => request<AdminStats>('/admin/stats'),
    system: () => request<AdminSystemStatus>('/admin/system'),

    users: (params: Record<string, string | number | undefined>) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      return request<{
        items: AdminUserRow[];
        total: number;
        nextCursor: string | null;
      }>(`/admin/users?${qs}`);
    },

    updateUser: (
      id: string,
      data: {
        banned?: boolean;
        banReason?: string;
        active?: boolean;
        isAdmin?: boolean;
        staffRole?: StaffRole;
        role?: Role;
        profileType?: ProfileType;
        entityType?: EntityType;
      },
    ) =>
      request<AdminUserRow>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Задать конкретный пароль. Логин — если его ещё нет или он меняется. */
    setPassword: (id: string, password: string, login?: string) =>
      request<{ login: string }>(`/admin/users/${id}/password`, {
        method: 'POST',
        body: JSON.stringify({ password, ...(login ? { login } : {}) }),
      }),

    /** Посмотреть площадку глазами пользователя. Токен — на час и без правок. */
    viewAs: (id: string) =>
      request<{ token: string; name: string }>(`/admin/users/${id}/view-as`, {
        method: 'POST',
      }),

    /** Решение по проверке участника. Модератор и выше. */
    verify: (id: string, state: VerifyState, note?: string) =>
      request<{ ok: true }>(`/admin/users/${id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ state, ...(note ? { note } : {}) }),
      }),

    /** Рассылка. С `dryRun` только считает аудиторию и ничего не шлёт. */
    broadcast: (input: BroadcastInput) =>
      request<BroadcastResult>('/admin/broadcast', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    broadcasts: () => request<{ items: BroadcastRow[] }>('/admin/broadcast'),

    /** Убрать отправленное у всех получателей. */
    recallBroadcast: (id: string) =>
      request<RecallResult>(`/admin/broadcast/${id}/recall`, { method: 'POST' }),

    /** Поправить текст уже отправленного — у всех сразу. */
    editBroadcast: (id: string, subject: string | undefined, body: string) =>
      request<RecallResult>(`/admin/broadcast/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ subject, body }),
      }),

    /** Проверить, что бот может писать в этот чат. */
    testChat: (chatId: string) =>
      request<{ ok: boolean; title?: string; type?: string; error?: string }>(
        '/admin/broadcast/chat-test',
        { method: 'POST', body: JSON.stringify({ chatId }) },
      ),

    /* --- Мониторинг чатов логистов --- */

    sourceChats: () =>
      request<{ items: SourceChatRow[] }>('/admin/source-chats'),

    addSourceChat: (chatId: string, via: 'BOT' | 'USERBOT' = 'BOT') =>
      request<{ id: string }>('/admin/source-chats', {
        method: 'POST',
        body: JSON.stringify({ chatId, via }),
      }),

    /* --- userbot (аккаунт-читатель) --- */

    userbot: () => request<UserbotView>('/admin/userbot'),

    userbotCreds: (apiId: string, apiHash: string) =>
      request<{ ok: true }>('/admin/userbot/creds', {
        method: 'POST',
        body: JSON.stringify({ apiId, apiHash }),
      }),

    userbotLogin: (phone: string) =>
      request<{ ok: true }>('/admin/userbot/login', {
        method: 'POST',
        body: JSON.stringify({ phone }),
      }),

    userbotCode: (code: string) =>
      request<{ ok: true }>('/admin/userbot/code', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),

    userbotPassword: (password: string) =>
      request<{ ok: true }>('/admin/userbot/password', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    userbotLogout: () =>
      request<{ ok: true }>('/admin/userbot/logout', { method: 'POST' }),

    patchSourceChat: (id: string, data: { enabled?: boolean; autoImport?: boolean }) =>
      request<{ ok: true }>(`/admin/source-chats/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    removeSourceChat: (id: string) =>
      request<{ ok: true }>(`/admin/source-chats/${id}`, { method: 'DELETE' }),

    ingestFeed: (status?: string, cursor?: string) =>
      request<{ items: IngestedPostRow[]; nextCursor: string | null }>(
        `/admin/ingest?${new URLSearchParams({
          ...(status ? { status } : {}),
          ...(cursor ? { cursor } : {}),
        })}`,
      ),

    skipIngest: (id: string) =>
      request<{ ok: true }>(`/admin/ingest/${id}/skip`, { method: 'POST' }),

    reparseIngest: (id: string) =>
      request<{ ok: true; status: string }>(`/admin/ingest/${id}/reparse`, {
        method: 'POST',
      }),

    resetPassword: (id: string) =>
      request<{ login: string; password: string }>(
        `/admin/users/${id}/reset-password`,
        { method: 'POST' },
      ),

    updateContacts: (
      id: string,
      data: { login?: string; email?: string; phone?: string; tgUsername?: string },
    ) =>
      request<AdminUserRow>(`/admin/users/${id}/contacts`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    cargos: (status?: string) =>
      request<{ items: unknown[] }>(
        `/admin/cargos${status ? `?status=${status}` : ''}`,
      ),

    groups: () => request<{ items: GroupRow[] }>('/admin/groups'),

    createGroup: (data: Record<string, unknown>) =>
      request<GroupRow>('/admin/groups', { method: 'POST', body: JSON.stringify(data) }),

    updateGroup: (id: string, data: Record<string, unknown>) =>
      request<GroupRow>(`/admin/groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteGroup: (id: string) =>
      request<{ ok: true }>(`/admin/groups/${id}`, { method: 'DELETE' }),

    members: (groupId: string) =>
      request<{ items: GroupMemberRow[] }>(`/admin/groups/${groupId}/members`),

    addMembers: (groupId: string, userIds: string[]) =>
      request<{ added: number; requested: number }>(`/admin/groups/${groupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      }),

    removeMember: (groupId: string, userId: string) =>
      request<{ ok: true }>(`/admin/groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
      }),

    levels: () => request<{ items: ContractLevelRow[] }>('/admin/contract-levels'),

    createLevel: (data: Record<string, unknown>) =>
      request<ContractLevelRow>('/admin/contract-levels', {
        method: 'POST', body: JSON.stringify(data) }),

    deleteLevel: (id: string) =>
      request<{ ok: true }>(`/admin/contract-levels/${id}`, { method: 'DELETE' }),

    contracts: (params: Record<string, string | undefined> = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
      return request<{ items: ContractRow[] }>(`/admin/contracts?${qs}`);
    },

    createContract: (data: ContractInput) =>
      request<ContractRow>('/admin/contracts', {
        method: 'POST', body: JSON.stringify(data) }),

    updateContract: (id: string, data: Partial<ContractInput>) =>
      request<ContractRow>(`/admin/contracts/${id}`, {
        method: 'PATCH', body: JSON.stringify(data) }),

    deleteContract: (id: string) =>
      request<{ ok: true }>(`/admin/contracts/${id}`, { method: 'DELETE' }),

    contractMembers: (id: string) =>
      request<{ items: ContractMemberRow[] }>(`/admin/contracts/${id}/members`),

    addContractMembers: (id: string, userIds: string[]) =>
      request<{ added: number }>(`/admin/contracts/${id}/members`, {
        method: 'POST', body: JSON.stringify({ userIds }) }),

    removeContractMember: (id: string, userId: string) =>
      request<{ ok: true }>(`/admin/contracts/${id}/members/${userId}`, { method: 'DELETE' }),

    overrides: (id: string) =>
      request<{ items: ContractOverrideRow[] }>(`/admin/contracts/${id}/overrides`),

    addOverride: (id: string, data: Record<string, unknown>) =>
      request<ContractOverrideRow>(`/admin/contracts/${id}/overrides`, {
        method: 'POST', body: JSON.stringify(data) }),

    removeOverride: (id: string, overrideId: string) =>
      request<{ ok: true }>(`/admin/contracts/${id}/overrides/${overrideId}`, { method: 'DELETE' }),

    updateSettings: (data: Record<string, unknown>) =>
      request<PlatformSettings>('/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    faq: () => request<{ items: FaqSection[] }>('/admin/faq'),

    createFaq: (data: FaqSectionInput) =>
      request<FaqSection>('/admin/faq', { method: 'POST', body: JSON.stringify(data) }),

    updateFaq: (id: string, data: Partial<FaqSectionInput>) =>
      request<FaqSection>(`/admin/faq/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteFaq: (id: string) =>
      request<{ ok: true }>(`/admin/faq/${id}`, { method: 'DELETE' }),

    restoreFaq: () =>
      request<{ items: FaqSection[] }>('/admin/faq/restore-defaults', { method: 'POST' }),

    /** Компании: состояние из набора (по умолчанию все) и поиск. */
    companies: (state: CompanyState = '', q = '') =>
      request<{ items: AdminCompanyRow[] }>(
        `/admin/companies?${qs({ state, q })}`,
      ),

    updateCompany: (
      id: string,
      data: { verified?: boolean; isDirectShipper?: boolean },
    ) =>
      request<AdminCompanyRow>(`/admin/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /**
     * Журнал действий. Фильтры складываются: человек + семейство действий
     * («cargo.» с точкой на конце) + отрезок времени.
     */
    log: (q: {
      actorId?: string;
      targetId?: string;
      targetType?: ActionTarget;
      action?: string;
      adminOnly?: boolean;
      since?: string;
      limit?: number;
      cursor?: string;
    }) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) {
        if (v !== undefined && v !== '') p.set(k, String(v));
      }
      const qs = p.toString();
      return request<{ items: ActionLogRow[]; nextCursor: string | null }>(
        `/admin/log${qs ? `?${qs}` : ''}`,
      );
    },

    currencies: () => request<{ items: CurrencyRow[] }>('/admin/currencies'),

    updateCurrency: (
      code: string,
      data: {
        deal?: boolean;
        display?: boolean;
        active?: boolean;
        rateSource?: RateSource;
        manualRate?: number | null;
        sortOrder?: number;
      },
    ) =>
      request<CurrencyRow>(`/admin/currencies/${code}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Состав компании: места с ролями плюс привязанные по ИНН без места. */
    companyMembers: (id: string) =>
      request<{ items: AdminCompanyMemberRow[] }>(`/admin/companies/${id}/members`),

    updateCompanyMember: (
      id: string,
      userId: string,
      data: {
        roles?: CompanyRole[];
        canDrive?: boolean;
        canSeeRates?: boolean;
        canBook?: boolean;
        active?: boolean;
        detach?: boolean;
      },
    ) =>
      request<{ ok: true; roles?: CompanyRole[] }>(
        `/admin/companies/${id}/members/${userId}`,
        { method: 'PATCH', body: JSON.stringify(data) },
      ),
  },

  feed: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
    }
    return request<{
      items: CargoListItem[];
      /** Чем сужена лента правилами групп. Пусто — ограничений нет. */
      scope: FeedScope | null;
      nextCursor: string | null;
    }>(`/cargo?${qs}`);
  },

  /**
   * Точки для карты. Отдельный вызов, а не `feed` с большим лимитом:
   * на карте нужны все заявки разом, но без авторов и рейтингов.
   */
  cargoMap: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
    }
    return request<{ items: CargoMapPoint[]; capped: boolean }>(`/cargo/map?${qs}`);
  },

  /**
   * Маршрут по дорогам для линии на карте. `points: null` — сервис
   * маршрутизации не настроен или не ответил, интерфейс в этом случае
   * молча рисует прямую линию, как и раньше.
   */
  cargoRoute: (cargoId: string) =>
    request<{ points: { lat: number; lng: number }[] | null }>(
      `/cargo/${cargoId}/route`,
    ),

  cargo: (id: string) => request<CargoDetail>(`/cargo/${id}`),

  /** Правка опубликованной заявки. Доступна автору, пока её не взяли. */
  updateCargo: (id: string, data: UpdateCargoInput) =>
    request<{ ok: true; id: string }>(`/cargo/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /** Поиск места по названию: справочник + внешний геокодер. */
  geoSearch: (q: string) =>
    request<{ items: Place[]; source?: 'local' | 'geocoder' }>(
      `/geo/search?q=${encodeURIComponent(q)}`,
    ),

  /** Что находится в точке на карте. `place: null` — геокодер не настроен. */
  geoReverse: (lat: number, lng: number) =>
    request<{ place: Place | null }>(`/geo/reverse?lat=${lat}&lng=${lng}`),

  /** Предпросмотр наценки. Цена — в валюте заявки, сервер знает её курс. */
  feePreview: (cargoId: string, price: number) =>
    request<DealBreakdown>(`/cargo/${cargoId}/fee-preview?price=${price}`),

  myGroups: () =>
    request<{ items: { id: string; name: string; kind: GroupKind }[] }>('/my/groups'),

  createCargo: (data: CreateCargoInput) =>
    request<{ id: string; moderation?: boolean }>('/cargo', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  cancelCargo: (id: string) =>
    request<{ ok: true }>(`/cargo/${id}/cancel`, { method: 'POST' }),

  /* --- Быстрая заявка из текста --- */

  /** Разобрать текст в черновик (форма «вставь сообщение из чата»). */
  parseCargoDraft: (text: string) =>
    request<{ id: string; parsed: CargoDraftParsed }>('/cargo/drafts', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  /** Забрать черновик по ссылке из бота. */
  cargoDraft: (id: string) =>
    request<CargoDraftView>(`/cargo/drafts/${id}`),

  dropCargoDraft: (id: string) =>
    request<{ ok: true }>(`/cargo/drafts/${id}`, { method: 'DELETE' }),

  /* --- Модерация заявок (диспетчер) --- */

  cargoModeration: () =>
    request<{ items: (CargoListItem & { comment: string | null; advanceNote: string | null; customsNote: string | null; contactPhone: string | null })[] }>(
      '/cargo/moderation',
    ),

  moderateCargo: (id: string, decision: 'APPROVE' | 'REJECT', note?: string) =>
    request<{ ok: true; status: 'ACTIVE' | 'BLOCKED' }>(`/cargo/${id}/moderate`, {
      method: 'POST',
      body: JSON.stringify({ decision, note }),
    }),

  makeOffer: (cargoId: string, data: { price: number; vehicleId?: string; comment?: string }) =>
    request<{ id: string }>(`/cargo/${cargoId}/offers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  offers: (cargoId: string) =>
    request<{ items: Offer[] }>(`/cargo/${cargoId}/offers`),

  acceptOffer: (offerId: string) =>
    request<{ orderId: string }>(`/offers/${offerId}/accept`, { method: 'POST' }),

  orders: (activeOnly = false) =>
    request<{ items: Order[] }>(`/orders${activeOnly ? '?active=true' : ''}`),

  setOrderStatus: (id: string, status: string, comment?: string) =>
    request<Order>(`/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, comment }),
    }),

  confirmOrder: (id: string, comment?: string) =>
    request<Order>(`/orders/${id}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),

  orderHistory: (id: string) =>
    request<{ items: OrderStatusEventRow[] }>(`/orders/${id}/history`),

  orderTrack: (id: string) =>
    request<{ items: TrackPointRow[] }>(`/orders/${id}/track`),

  /** Экипаж компании: кто где и с чем. Только для управляющих ролей. */
  crew: (companyId: string) =>
    request<{ items: CrewRow[] }>(`/companies/${companyId}/crew`),

  /** Парк компании: машины и прицепы отдельными единицами. */
  fleet: (companyId: string) =>
    request<{ items: FleetRow[] }>(`/companies/${companyId}/fleet`),
  /** История заявки: правки, отклики, файлы. Только сторонам сделки. */
  currencies: () => request<{ items: CurrencyOption[] }>('/currencies'),

  cargoHistory: (id: string) =>
    request<{ items: CargoHistoryRow[] }>(`/cargo/${id}/history`),

  /**
   * Мои живые торги — для полоски обратного отсчёта при входе.
   *
   * `now` — время сервера: по нему выставляются часы, иначе таймер
   * покажет то, чего нет (см. `serverNow` в realtime.ts).
   */
  myBids: () => request<{ now: number; items: MyBidRow[] }>('/cargo/my-bids'),

  fleetMatches: (companyId: string) =>
    request<{ items: FleetMatchRow[] }>(`/companies/${companyId}/matches`),

  /** Закрепить экипаж, прицепить прицеп, править паспорт, вывести из парка. */
  patchVehicle: (id: string, data: PatchVehicleInput) =>
    request<{ ok: true }>(`/vehicles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Удалить технику. Если по ней были рейсы — сервер выведет её из парка
   * вместо удаления и скажет об этом полем `kept`.
   */
  deleteVehicle: (id: string) =>
    request<{ ok: true; kept: boolean }>(`/vehicles/${id}`, { method: 'DELETE' }),

  vehicleFiles: (id: string) =>
    request<{ items: FileRow[] }>(`/vehicles/${id}/files`),

  /** История рейсов машины: что возила и сколько это стоило. */
  vehicleTrips: (id: string) =>
    request<{ items: VehicleTripRow[] }>(`/vehicles/${id}/trips`),

  /** Сводка по компании: люди, техника, рейсы, деньги. */
  companyOverview: (companyId: string) =>
    request<CompanyOverview>(`/companies/${companyId}/overview`),

  /** Перегруз: передать рейс другой машине или водителю. */
  reassignOrder: (orderId: string, data: ReassignOrderInput) =>
    request<{ id: string }>(`/orders/${orderId}/reassign`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Завести сотрудника. Пароль возвращается один раз — показать и отдать. */
  createMember: (companyId: string, data: CompanyMemberCreateInput) =>
    request<{ userId: string; login: string; password: string }>(
      `/companies/${companyId}/members`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  /** Разовая отметка «я здесь». Постоянную трансляцию ведёт бот. */
  sendTrackPoint: (id: string, lat: number, lng: number) =>
    request<{ ok: true }>(`/orders/${id}/track`, {
      method: 'POST',
      body: JSON.stringify({ lat, lng }),
    }),

  addVehicle: (data: Record<string, unknown>) =>
    request<Vehicle>('/vehicles', { method: 'POST', body: JSON.stringify(data) }),

  addSubscription: (data: Record<string, unknown>) =>
    request<Subscription>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteSubscription: (id: string) =>
    request<{ ok: true }>(`/subscriptions/${id}`, { method: 'DELETE' }),

  /* ---------- Файлы ---------- */

  /**
   * Загрузка. FormData отправляем напрямую, минуя request():
   * границу multipart проставляет браузер, и свой Content-Type
   * здесь всё сломает.
   */
  async upload(
    file: File,
    q: {
      kind: string;
      orderId?: string;
      cargoId?: string;
      vehicleId?: string;
      docType?: string;
    },
  ): Promise<FileRow> {
    const form = new FormData();
    form.append('file', file);

    const params = new URLSearchParams({ kind: q.kind });
    if (q.orderId) params.set('orderId', q.orderId);
    if (q.cargoId) params.set('cargoId', q.cargoId);
    if (q.vehicleId) params.set('vehicleId', q.vehicleId);
    if (q.docType) params.set('docType', q.docType);

    const res = await fetch(`${BASE}/files?${params}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new ApiError(body.error ?? `Ошибка ${res.status}`, res.status);
    return body as unknown as FileRow;
  },

  myDocs: () => request<{ items: FileRow[] }>('/files/my-docs'),

  userDocs: (userId: string) => request<{ items: FileRow[] }>(`/users/${userId}/docs`),

  orderFiles: (orderId: string) => request<{ items: FileRow[] }>(`/orders/${orderId}/files`),

  /** Что ещё есть по этому направлению. */
  similarCargos: (id: string) => request<SimilarCargos>(`/cargo/${id}/similar`),

  cargoFiles: (cargoId: string) => request<{ items: FileRow[] }>(`/cargo/${cargoId}/files`),

  /* ---------- Проверка профиля и рассылки ---------- */

  /** Подать заявку на ручную проверку. */
  requestVerify: () => request<{ ok: true }>('/me/verify', { method: 'POST' }),

  /** Свои каналы уведомлений. */
  setNotifyPrefs: (prefs: Partial<NotifyPrefs>) =>
    request<NotifyPrefs>('/me/notifications', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    }),

  /* ---------- Диспетчерская ---------- */

  /** Все рейсы площадки. Доступно поддержке и выше, остальным — 404. */
  dispatch: (q: DispatchQuery) =>
    request<DispatchBoard>(`/dispatch/orders?${qs(q)}`),

  /* ---------- Аналитика ---------- */

  analyticsMeta: (q: AnalyticsQuery = {}) =>
    request<AnalyticsMeta>(`/analytics/meta?${qs(q)}`),

  analyticsReport: (q: AnalyticsQuery) =>
    request<AnalyticsReport>(`/analytics/report?${qs(q)}`),

  analyticsDeals: (q: AnalyticsQuery) =>
    request<{ items: DealRow[] }>(`/analytics/deals?${qs(q)}`),

  analyticsLeaders: (q: AnalyticsQuery = {}) =>
    request<LeaderBoard>(`/analytics/leaders?${qs(q)}`),

  /**
   * Выгрузка.
   *
   * Не `<a href>`: файл отдаётся по токену в заголовке, а браузер его туда
   * не подставит. Поэтому качаем сами и подсовываем blob.
   */
  async exportCsv(kind: ExportKind, q: AnalyticsQuery): Promise<void> {
    const res = await fetch(`${BASE}/analytics/export?${qs({ ...q, kind })}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(body.error ?? `Ошибка ${res.status}`, res.status);
    }

    saveBlob(await res.blob(), fileNameFrom(res) ?? `${kind}.csv`);
  },

  /** Скачивание вложения по подписанной ссылке — с исходным именем файла. */
  async download(url: string, name: string): Promise<void> {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(`Ошибка ${res.status}`, res.status);

    saveBlob(await res.blob(), name);
  },

  /* ---------- Функции площадки ---------- */

  features: () => request<FeatureMap>('/features'),

  /** Живые счётчики площадки для шапки. */
  pulse: () => request<PlatformPulse>('/pulse'),

  /* ---------- Уведомления ---------- */

  notifications: () =>
    request<{ items: NotificationRow[]; unread: number }>('/notifications'),

  /** Счётчики значков: колокольчик и вкладка «Рейсы». */
  unreadCount: () => request<{ unread: number; orders: number }>('/notifications/unread'),

  /** Раздел «Рейсы» открыт — значок сбрасывается. */
  markOrdersSeen: () => request<{ ok: true; orders: number }>('/orders/seen', { method: 'POST' }),

  /** Настраиваемые параметры площадки: что задано и откуда. */
  secrets: () => request<{ items: SecretRow[] }>('/admin/secrets'),

  /** Задать параметр. Пустое значение возвращает его к окружению. */
  setSecret: (key: string, value: string) =>
    request<{ ok: true }>('/admin/secrets', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    }),

  /** Состояние бота и канала до Telegram — только администратору. */
  botStatus: () =>
    request<{ online: boolean; username: string | null; error: string | null; apiRoot: string }>(
      '/admin/bot',
    ),

  /** Проверить адрес Bot API, не сохраняя его. */
  botTest: (apiRoot: string) =>
    request<{ ok: boolean; username?: string; error?: string; apiRoot: string }>(
      '/admin/bot/test',
      { method: 'POST', body: JSON.stringify({ apiRoot }) },
    ),

  readNotifications: (ids?: string[]) =>
    request<{ unread: number }>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  clearNotifications: () =>
    request<{ ok: true }>('/notifications', { method: 'DELETE' }),

  /* ---------- Администрирование ---------- */

  deleteUser: (id: string) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),

  deleteCargo: (id: string, reason?: string) =>
    request<{ ok: true; blocked: boolean }>(
      `/admin/cargos/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
      { method: 'DELETE' },
    ),

  adminFeatures: () => request<{ items: FeatureRuleRow[] }>('/admin/features'),

  setFeature: (data: Record<string, unknown>) =>
    request<{ id: string }>('/admin/features', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteFeature: (id: string) =>
    request<{ ok: true }>(`/admin/features/${id}`, { method: 'DELETE' }),

  setProfileType: (profileType: ProfileType) =>
    request<{ profileType: ProfileType; role: Role }>('/me/profile-type', {
      method: 'POST',
      body: JSON.stringify({ profileType }),
    }),

  linkByCode: (code: string) =>
    request<{ ok: true; login: string | null }>('/me/link/code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  deleteFile: (id: string) => request<{ ok: true }>(`/files/${id}`, { method: 'DELETE' }),

  /* ---------- Компании ---------- */

  myCompanies: () => request<{ items: MyCompanyRow[] }>('/companies/my'),

  claimCompany: () =>
    request<{ companyId: string }>('/companies/claim', { method: 'POST' }),

  companyMembers: (id: string) =>
    request<{ items: CompanyMemberRow[] }>(`/companies/${id}/members`),

  companyDrivers: (id: string) =>
    request<{ items: { id: string; name: string }[] }>(`/companies/${id}/drivers`),

  createInvite: (id: string, data: Record<string, unknown>) =>
    request<{ code: string; expiresAt: string; role: string }>(
      `/companies/${id}/invites`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  joinCompany: (code: string) =>
    request<{ companyId: string; name: string }>('/companies/join', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  patchMember: (id: string, userId: string, data: Record<string, unknown>) =>
    request<CompanyMemberRow>(`/companies/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  assignDriver: (orderId: string, driverId: string) =>
    request<{ id: string; driverId: string }>(`/orders/${orderId}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ driverId }),
    }),

  /* ---------- Деньги рейса ---------- */

  expenses: (orderId: string) =>
    request<{ items: ExpenseRow[]; economics: OrderEconomics }>(
      `/orders/${orderId}/expenses`,
    ),

  addExpense: (orderId: string, data: ExpenseInput & { fileId?: string }) =>
    request<ExpenseRow>(`/orders/${orderId}/expenses`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteExpense: (id: string) =>
    request<{ ok: true }>(`/expenses/${id}`, { method: 'DELETE' }),

  summary: (
    q: { from?: string; to?: string; companyId?: string; as?: string } = {},
  ) => {
    const params = new URLSearchParams(
      Object.entries(q).filter(([, v]) => v) as [string, string][],
    );
    return request<PeriodSummary>(`/orders/summary?${params}`);
  },
};

/** Подписанный адрес приходит от сервера без префикса — дописываем базу. */
export function fileUrl(row: FileRow): string {
  return `${BASE}${row.url}`;
}
